import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, timer } from 'rxjs';
import { catchError, map, retryWhen, mergeMap, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { AuthService, ResponseObject } from 'src/app/Services/auth.service';
import { ServiceBase } from 'src/app/Services/service.base';

/**
 * AI image enhancement — shadows and scene placement.
 *
 * **This no longer calls the provider directly.** It used to POST to the vendor from the browser with
 * `environment.briaApiKey` in an `api_token` header, which put a live credential in the JS bundle of
 * every deployed environment — readable by anyone who opened devtools. Rotating the key would not
 * have helped, because the replacement shipped to the browser too. All calls now go to the ArtFlow
 * API, which holds the key server-side and names the OUTCOME rather than the vendor.
 *
 * NAMING DEBT (deliberate, registered): the class and file are still `BriaAiService` /
 * `bria-ai.service.ts`, which is now a misnomer — nothing here is vendor-specific. Renaming touches
 * the image editor's imports and is deferred to the phase that introduces provider binding, rather
 * than mixed into the change that closes a live credential exposure.
 */

/** Normalised shadow result. Field names kept snake_case so the editor is unchanged. */
export interface BriaShadowResponse {
  result_url: string;
}

export interface BriaLifestyleShotResponse {
  result_url: string;
  seed?: number;
  session_id?: string;
}

/** What the API returns inside `ResponseObject.ResponseData` (PascalCase, per the C# contract). */
interface ImageEnhanceResult {
  Success: boolean;
  ResultUrl: string;
  Seed?: number;
  SessionId?: string;
  ErrorMessage?: string;
}

export interface BriaErrorResponse {
  error?: string;
  message?: string;
  detail?: string;
}

export interface BriaTrialStatus {
  used: number;
  limit: number;
  remaining: number;
  isTrialActive: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class BriaAiService extends ServiceBase {
  private readonly api: string = '/ImageEnhance';
  private readonly TRIAL_LIMIT = 9999; // TODO: Change back to 10 for production
  private readonly STORAGE_KEY = 'artflow-bria-trial';

  constructor(private http: HttpClient, authService: AuthService) {
    super(authService);
  }

  /**
   * Get current trial status for the user.
   *
   * NOTE: this is a localStorage counter and is trivially reset by the user. It is a courtesy limit,
   * not a spend control — the real ceiling belongs server-side with the other quotas.
   */
  getTrialStatus(userId: string): BriaTrialStatus {
    const key = `${this.STORAGE_KEY}-${userId}`;
    const stored = localStorage.getItem(key);
    const used = stored ? parseInt(stored, 10) : 0;

    return {
      used,
      limit: this.TRIAL_LIMIT,
      remaining: Math.max(0, this.TRIAL_LIMIT - used),
      isTrialActive: used < this.TRIAL_LIMIT
    };
  }

  private incrementTrialUsage(userId: string): void {
    const key = `${this.STORAGE_KEY}-${userId}`;
    const stored = localStorage.getItem(key);
    const current = stored ? parseInt(stored, 10) : 0;
    localStorage.setItem(key, (current + 1).toString());
  }

  canUseAiBackground(userId: string): boolean {
    return this.getTrialStatus(userId).isTrialActive;
  }

  /**
   * Unwrap the standard envelope. HTTP 200 carrying `Success: false` is the normal failure mode on
   * this API, so a status check alone would treat every handled error as a success.
   */
  private unwrap(response: ResponseObject): ImageEnhanceResult {
    if (!response?.Success) {
      throw new Error(response?.ResponseMessage || 'The image could not be processed.');
    }

    const result = response.ResponseData as ImageEnhanceResult;
    if (!result?.ResultUrl) {
      throw new Error(result?.ErrorMessage || 'No image was returned.');
    }

    return result;
  }

  /**
   * Add a shadow beneath a cut-out.
   *
   * @param imageInput data URL from the canvas, or a publicly reachable image URL. The server decides
   *                   which payload shape the provider needs; the client no longer strips prefixes.
   */
  addShadow(
    imageInput: string,
    options: {
      shadowType?: 'regular' | 'float';
      intensity?: number;
      blur?: number;
      position?: [number, number];
      width?: number;
      height?: number;
      color?: string;
    } = {}
  ): Observable<BriaShadowResponse> {
    const url = environment.BASE_API_PATH + this.api + '/shadow';

    // PascalCase: these bind to a C# DTO. camelCase would silently arrive as null.
    const body = {
      Image: imageInput,
      ShadowType: options.shadowType || 'float',
      Intensity: options.intensity,
      Blur: options.blur,
      Position: options.position,
      Width: options.width,
      Height: options.height,
      Color: options.color
    };

    return this.http.post<ResponseObject>(url, body, this.RequestHeaders())
      .pipe(
        map((response: ResponseObject) => ({ result_url: this.unwrap(response).ResultUrl })),
        this.handleRetry<BriaShadowResponse>(),
        catchError(this.handleError)
      );
  }

  /** Place the work in a described setting. */
  generateLifestyleShot(
    imageInput: string,
    sceneDescription: string,
    userId: string
  ): Observable<BriaLifestyleShotResponse> {
    if (!this.canUseAiBackground(userId)) {
      return throwError(() => new Error('Trial limit reached. Please upgrade to continue using AI backgrounds.'));
    }

    const url = environment.BASE_API_PATH + this.api + '/scene';

    const body = {
      Image: imageInput,
      SceneDescription: sceneDescription
    };

    return this.http.post<ResponseObject>(url, body, this.RequestHeaders())
      .pipe(
        map((response: ResponseObject): BriaLifestyleShotResponse => {
          const result = this.unwrap(response);
          return {
            result_url: result.ResultUrl,
            seed: result.Seed,
            session_id: result.SessionId
          };
        }),
        tap(() => {
          // Only count a generation that actually produced an image.
          this.incrementTrialUsage(userId);
        }),
        this.handleRetry<BriaLifestyleShotResponse>(),
        catchError(this.handleError)
      );
  }

  /**
   * Shadow first, then scene — the complete workflow. The shadow step returns a provider-hosted URL
   * which is fed straight into the scene step, exactly as before.
   */
  generateWithShadow(
    imageUrl: string,
    sceneDescription: string,
    userId: string,
    addShadow: boolean = true,
    shadowOptions?: {
      shadowType?: 'regular' | 'float';
      intensity?: number;
      blur?: number;
      position?: [number, number];
      width?: number;
      height?: number;
    }
  ): Observable<BriaLifestyleShotResponse> {
    if (!this.canUseAiBackground(userId)) {
      return throwError(() => new Error('Trial limit reached. Please upgrade to continue using AI backgrounds.'));
    }

    if (!addShadow) {
      return this.generateLifestyleShot(imageUrl, sceneDescription, userId);
    }

    return this.addShadow(imageUrl, shadowOptions || { shadowType: 'float' }).pipe(
      mergeMap(shadowResult => this.generateLifestyleShot(shadowResult.result_url, sceneDescription, userId))
    );
  }

  /** Convert canvas to a base64 data URL for submission. */
  imageToBase64(canvas: HTMLCanvasElement): string {
    return canvas.toDataURL('image/png');
  }

  /** Download a generated image (provider URLs expire after about an hour). */
  downloadImage(url: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Failed to download generated image'));
      img.src = url;
    });
  }

  /** Retry once on a 5xx, which is the only class of failure worth retrying automatically. */
  private handleRetry<T>(): (source: Observable<T>) => Observable<T> {
    return (source: Observable<T>) =>
      source.pipe(
        retryWhen(errors =>
          errors.pipe(
            mergeMap((error: HttpErrorResponse, index) => {
              const retryAttempt = index + 1;
              if (retryAttempt > 1 || error.status !== 500) {
                return throwError(() => error);
              }
              return timer(2000);
            })
          )
        )
      );
  }

  /**
   * Transport-level failures only. Provider errors are already translated server-side and arrive as
   * `Success: false` with a message an artist can act on, thrown by `unwrap`.
   */
  private handleError = (error: HttpErrorResponse | Error): Observable<never> => {
    if (error instanceof HttpErrorResponse) {
      console.error('Image enhancement transport error:', { status: error.status, message: error.message });

      if (error.status === 401) {
        return throwError(() => new Error('Your session has expired. Please sign in again.'));
      }
      if (error.status === 413) {
        return throwError(() => new Error('Image is too large. Please use an image under 12MB.'));
      }
      return throwError(() => new Error('An error occurred while generating the background.'));
    }

    return throwError(() => error);
  };

  readonly scenePresets = [
    { name: 'Studio White', prompt: 'Clean white studio background with soft professional lighting' },
    { name: 'Natural Light', prompt: 'Natural daylight streaming through window, soft shadows on neutral surface' },
    { name: 'Marble Surface', prompt: 'Elegant white marble surface with subtle veining, minimalist setting' },
    { name: 'Wood Table', prompt: 'Warm rustic wooden table surface with natural grain, cozy ambient lighting' },
    { name: 'Outdoor Garden', prompt: 'Lush green garden setting with soft bokeh, natural sunlight' },
    { name: 'Modern Interior', prompt: 'Contemporary minimalist interior, clean lines, neutral tones' },
    { name: 'Cozy Home', prompt: 'Warm cozy living room setting with soft textiles and warm lighting' },
    { name: 'Art Gallery', prompt: 'Modern art gallery white wall, professional museum lighting' },
  ];
}
